import { getDevicePref, setDevicePref } from "@eventr/common";
import { deviceDetect } from "react-device-detect";
import { create } from "zustand";
import { getDevicePrefName } from "../../utils";

const PREF = "twoPaneLayout";

/** The setting on a device where nobody has changed it. */
export const TWO_PANE_DEFAULT = true;

interface TwoPaneSettingState {
  /** Whether this device allows the two-pane layout on wide windows. */
  twoPaneSetting: boolean;
  setTwoPaneSetting: (twoPaneSetting: boolean) => void;
}

const device = getDevicePrefName(deviceDetect);

// A device preference, not reset on logout.
export const useTwoPaneSettingStore = create<TwoPaneSettingState>()((set) => ({
  twoPaneSetting: (getDevicePref(PREF, device, undefined) as boolean | undefined) ?? TWO_PANE_DEFAULT,
  setTwoPaneSetting: (twoPaneSetting) => {
    // `setDevicePref` types its value as a string but stores it as JSON.
    setDevicePref(PREF, twoPaneSetting as unknown as string, device);
    set({ twoPaneSetting });
  },
}));

export const useTwoPaneSetting = () => useTwoPaneSettingStore((s) => s.twoPaneSetting);

const SCREEN_PREF = "twoPaneScreen";

/**
 * The shortest side of a screen with room for two panes, whichever way up. Under iOS native
 * navigation UIKit splits a window that is regular in both size classes: an iPad, an unfolded
 * iPhone Duo. Every other iPhone is at most 440pt across.
 */
const TWO_PANE_SCREEN_MIN_SIDE = 640;

const screenHasRoom = () => Math.min(window.screen.width, window.screen.height) >= TWO_PANE_SCREEN_MIN_SIDE;

interface TwoPaneScreenState {
  /** Whether this device's screen has had room for two panes, now or on an earlier launch. */
  seen: boolean;
  check: () => void;
}

// Remembered, so a folded iPhone Duo, whose outer screen is phone-sized, still offers the setting.
export const useTwoPaneScreenStore = create<TwoPaneScreenState>()((set, get) => ({
  seen: (getDevicePref(SCREEN_PREF, device, undefined) as boolean | undefined) ?? false,
  check: () => {
    if (get().seen || !screenHasRoom()) return;
    setDevicePref(SCREEN_PREF, true as unknown as string, device);
    set({ seen: true });
  },
}));

useTwoPaneScreenStore.getState().check();
// Unfolding changes the screen and resizes the window.
window.addEventListener("resize", () => useTwoPaneScreenStore.getState().check());

/** Whether this device's screen can show two panes, so the two-pane setting does something. */
export const useTwoPaneScreen = () => useTwoPaneScreenStore((s) => s.seen);
